import { createRef, useEffect, useMemo, useRef, useState } from 'react';
import tw from 'twin.macro';
import ReactDatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { isNumber } from 'lodash';

import { useIntegrationsContext } from '@insights_module/context/IntegrationsContext';
import { Input } from '@insights_module/components/Input';
import { SegmentCreator } from '@insights_module/components/SegmentCreator';
import { useNotification } from '@insights_module/hooks/useNotification';
import { useIntegrationTags } from '@insights_module/hooks/useIntegrationTags';
import { CheckMarkIcon } from '@icons/CheckMarkIcon';
import { CopyIcon } from '@icons/CopyIcon';
import { useSegmentCreatorContext } from '@insights_module/context/SegmentCreatorContext';
import {
  buildQueryFromQueryData,
  createQueryDataFromQuery,
  groupQueryDataByGroupId,
} from '@insights_module/adc_ql/utils';
import { Spacer } from '@components/Spacer';
import { IntegrationType } from '@insights_module/types/Integrations';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { Button } from '@components/Button';
import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import { CalendarIcon } from '@icons/CalendarIcon';
import { EditIcon } from '@icons/EditIcon';
import { DatePicker } from '@components/DatePicker';
import {
  AdPlacementLogicType,
  useFlightpathAvails,
} from '@insights_module/hooks/useFlightpathAvails';
import { Loader } from '@components/Loader';
import { AQLFlatQueryBuilder } from '@insights_module/adc_ql/QueryBuilder';
import { addDaysToDate } from '@utils/dateUtils';
import { updateIntegrationTagDescription } from '@insights_module/services/megaphone';
import {
  INTEGRATION_QUERY_KEYS,
  INTEGRATION_TYPE_MAPPER,
} from '@insights_module/constants/integrations';

const Wrapper = tw.div`w-full h-full flex flex-col p-6 bg-page-background text-left`;
const Title = tw.div`text-sm font-semibold text-text-and-icon`;
const Description = tw.div`text-[13px] text-text-and-icon-muted`;
const Code = tw.span`text-dark-accent-invert uppercase text-xs font-semibold whitespace-nowrap`;
const Flex = tw.div`flex items-center`;
const TagInfoContainer = tw.div`w-full px-4`;
const ActionButton = tw.button`border border-solid border-gray-1 rounded p-1 active:bg-gray-2 active:opacity-70`;
const TextArea = tw.textarea`block w-full bg-page-background rounded-[8px] border border-solid border-gray-1 p-[12px] text-[15px] text-text-and-icon-muted`;

const ControlButton = tw(
  Button.Primary
)`text-xs leading-none px-3 py-[5px] w-auto h-[28px] border border-solid border-gray-1 align-middle`;
const FromInnerWrapper = tw.div`flex flex-col md:flex-row bg-page-background`;
const FormInputsWrapper = tw.div`flex flex-col w-full`;
const FormLabel = tw.label`text-xs font-semibold text-text-and-icon-muted`;
const FormInputsContainer = tw.div`w-full p-6 bg-page-background`;
const FormInputs = tw.div`w-full px-4`;
const Subtitle = tw.span`text-sm text-text-and-icon text-left font-semibold w-full h-[38px] py-[10px] px-[24px] border-b border-gray-1`;

const campaignDurationOptions: IDropdownOption[] = [
  { value: '30-days', label: <Option>Next 30 days</Option> },
  { value: '60-days', label: <Option>Next 60 days</Option> },
  { value: '90-days', label: <Option>Next 90 days</Option> },
  {
    value: 'custom',
    label: (
      <Option style={{ display: 'flex', justifyContent: 'space-between' }}>
        Custom <CalendarIcon stroke='var(--text-and-icon)' />{' '}
      </Option>
    ),
  },
];

const insertionOptions: IDropdownOption[] = [
  { value: 'pre', label: <Option>Pre</Option> },
  { value: 'mid', label: <Option>Mid</Option> },
  { value: 'post', label: <Option>Post</Option> },
  { value: 'pre-mid', label: <Option>Pre + Mid</Option> },
  { value: 'mid-post', label: <Option>Mid + Post</Option> },
  { value: 'pre-post', label: <Option>Pre + Post</Option> },
  { value: 'pre-mid-post', label: <Option>Pre + Mid + Post</Option> },
  { value: 'pre-mid_or', label: <Option>Pre or Mid</Option> },
  { value: 'mid-post_or', label: <Option>Mid or Post</Option> },
  { value: 'pre-post_or', label: <Option>Pre or Post</Option> },
  { value: 'pre-mid-post_or', label: <Option>Pre or Mid or Post</Option> },
];

export const ViewTag = ({ type }: { type: IntegrationType }) => {
  const queryClient = useQueryClient();
  const { isEnabled: isFlightpathEnabled } = useFeatureFlag(
    FEATURE_FLAG.MEGAPHONE_FLIGHTPATH_INTEGRATION
  );
  const { t } = useTranslation();
  const { notify, NTFC_TYPE } = useNotification();
  const { tagToEdit, searchTerm } = useIntegrationsContext();
  const { setSegmentsData, setIsPresentational, segmentsData } = useSegmentCreatorContext();
  const { getTagById } = useIntegrationTags(type, searchTerm);
  const tag = getTagById(tagToEdit);
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null);
  const { id, display_name, description, aql_query } = tag?.query || {};
  const { initiateFlightpathAvails, totalAvails, isLoading } = useFlightpathAvails();

  const [copiedTag, setCopiedTag] = useState<boolean>(false);
  const datePickerRef = createRef<ReactDatePicker>();

  const [campaignDuration, setCampaignDuration] = useState<IDropdownOption>(
    campaignDurationOptions[0]
  );
  const [insertion, setInsertion] = useState<IDropdownOption>(insertionOptions[0]);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [currentParams, setCurrentParams] = useState<string | null>(null);
  const [lastSentParams, setLastSentParams] = useState<string | null>(null);
  const [capDaysInterval, setCapDaysInterval] = useState<string | undefined>(undefined);
  const [capMaxImpressions, setCapMaxImpressions] = useState<string | undefined>(undefined);
  const [isEditingDescription, setIsEditingDescription] = useState<boolean>(false);
  const [isDescriptionLoading, setIsDescriptionLoading] = useState<boolean>(false);

  const handleCopyToClipboard = () => {
    if (navigator && navigator.clipboard && id) {
      navigator.clipboard.writeText(id);
      notify({
        type: NTFC_TYPE.INFO,
        message: 'Copied to clipboard',
        duration: 1000,
      });
      setCopiedTag(true);
      setTimeout(() => setCopiedTag(false), 1000);
    }
  };

  const handleDateRangeChange = (val: IDropdownOption) => {
    if (val.value === 'custom') {
      datePickerRef.current?.setOpen(true);
    }

    setCampaignDuration(val as IDropdownOption);
  };

  const handleCalculateAvails = async (e: MouseEvent) => {
    e.preventDefault();

    if (!currentParams) return;
    initiateFlightpathAvails({ ...JSON.parse(currentParams) });
    setLastSentParams(currentParams);
  };

  const isFlightpathCalculationEnabled = useMemo(() => {
    return Boolean(
      startDate &&
        endDate &&
        insertion &&
        segmentsData.length > 1 &&
        !isLoading &&
        currentParams !== lastSentParams
    );
  }, [
    startDate,
    endDate,
    insertion,
    segmentsData,
    capDaysInterval,
    capMaxImpressions,
    isLoading,
    currentParams,
    lastSentParams,
  ]);

  useEffect(() => setIsPresentational(true), []);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (aql_query) {
      const tagSegmentsData = createQueryDataFromQuery([aql_query], null);

      // We need to set the segments data after initially is set from context/url params
      timeout = setTimeout(() => setSegmentsData(tagSegmentsData), 0);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [aql_query]);

  useEffect(() => {
    if (campaignDuration.value === 'custom') {
      datePickerRef.current?.setOpen(true);
    }

    if (campaignDuration.value === '30-days') {
      setStartDate(new Date());
      setEndDate(addDaysToDate(30));
    }
    if (campaignDuration.value === '60-days') {
      setStartDate(new Date());
      setEndDate(addDaysToDate(60));
    }
    if (campaignDuration.value === '90-days') {
      setStartDate(new Date());
      setEndDate(addDaysToDate(90));
    }
  }, [campaignDuration.value]);

  useEffect(() => {
    const [startCustomDate, endCustomDate] = dateRange;
    if (startCustomDate && endCustomDate) {
      const days = Math.ceil(
        (endCustomDate.getTime() - startCustomDate.getTime()) / (1000 * 3600 * 24)
      );

      setCampaignDuration({
        value: `${days}-days`,
        label: (
          <Option>
            {startCustomDate.toLocaleDateString()} - {endCustomDate.toLocaleDateString()}
          </Option>
        ),
      });
      setStartDate(startCustomDate);
      setEndDate(endCustomDate);
    }
  }, [dateRange]);

  useEffect(() => {
    if (!startDate || !endDate || !insertion || !(segmentsData.length > 1)) return;
    const builder = new AQLFlatQueryBuilder();
    buildQueryFromQueryData(groupQueryDataByGroupId(segmentsData), builder, null);
    const query = builder.build();

    const isORLogic = Boolean(insertion?.value && insertion.value.includes('_or'));
    const AdPlacementLogic: AdPlacementLogicType = isORLogic ? 'OR' : 'AND';

    let insertions: string[] = [];

    if (insertion?.value) {
      insertions = isORLogic
        ? insertion.value.replace('_or', '').split('-')
        : insertion.value.split('-');
    }

    const flightpath_params = {
      start_date: startDate.toISOString().substring(0, 10),
      end_date: endDate.toISOString().substring(0, 10),
      insertions,
      cap_days_interval: capDaysInterval,
      cap_max_impressions: capMaxImpressions,
      ad_placement_logic: AdPlacementLogic,
    };

    setCurrentParams(JSON.stringify({ flightpath_params, query }));
  }, [startDate, endDate, insertion, segmentsData, capDaysInterval, capMaxImpressions]);

  const handleUpdateDescription = async () => {
    if (!descriptionInputRef?.current?.value || !tag?.query.id) return;
    setIsDescriptionLoading(true);

    updateIntegrationTagDescription(tag?.query.id, {
      description: descriptionInputRef?.current?.value,
    })
      .then(() =>
        queryClient.invalidateQueries([
          INTEGRATION_QUERY_KEYS.INFINITE_TAGS,
          INTEGRATION_TYPE_MAPPER[type],
        ])
      )
      .catch(() =>
        notify({
          type: NTFC_TYPE.WARNING,
          message: 'Error while updating tag description',
          duration: 2000,
        })
      )
      .finally(() => {
        setIsDescriptionLoading(false);
        setIsEditingDescription(false);
      });
  };

  if (type === 'megaphone' && isFlightpathEnabled) {
    return (
      <FromInnerWrapper>
        <FormInputsWrapper>
          <Subtitle>Tag Query</Subtitle>
          <Wrapper>
            <TagInfoContainer>
              <Title>{display_name || ''}</Title>
              <Spacer direction='vertical' size='xs' />
              {isEditingDescription ? (
                <>
                  <TextArea ref={descriptionInputRef} defaultValue={description || ''} />
                  <Spacer direction='vertical' size='xs' />
                  <div tw='w-full flex items-center justify-end gap-[8px]'>
                    <Button.Secondary
                      tw='text-text-and-icon bg-transparent border-gray-1 shadow-none'
                      disabled={isDescriptionLoading}
                      onClick={() => setIsEditingDescription(!isEditingDescription)}>
                      Cancel
                    </Button.Secondary>
                    <Button.Primary
                      tw='bg-accent-2 text-text-and-icon-invert shadow-none'
                      onClick={handleUpdateDescription}
                      disabled={isDescriptionLoading}>
                      {isDescriptionLoading ? <Loader theme='dark' size='xs' /> : 'Save Edits'}
                    </Button.Primary>
                  </div>
                </>
              ) : (
                <div tw='flex gap-[8px]'>
                  <Description>{description || 'No description'}</Description>{' '}
                  <button onClick={() => setIsEditingDescription(!isEditingDescription)}>
                    <EditIcon tw='stroke-text-and-icon-invert cursor-pointer' />
                  </button>
                </div>
              )}

              <Spacer direction='vertical' size='sm' />
              <Flex>
                <Code>{id}</Code>
                <ActionButton tw='ml-2' onClick={handleCopyToClipboard}>
                  {copiedTag ? (
                    <CheckMarkIcon tw='stroke-dark-accent-invert w-4 h-4' />
                  ) : (
                    <CopyIcon tw='stroke-dark-accent-invert' />
                  )}
                </ActionButton>
              </Flex>
            </TagInfoContainer>

            <SegmentCreator />
          </Wrapper>
          <div tw='flex min-h-[52px] bg-neutral-background'></div>
        </FormInputsWrapper>
        <div tw='min-h-full w-[1px] bg-gray-1' />
        <div tw='flex flex-col w-full max-w-[500px] justify-between'>
          <div tw='flex flex-col'>
            <Subtitle>Flightpath Parameters</Subtitle>
            <FormInputsContainer>
              <FormInputs tw='p-[8px] flex flex-col gap-[24px]'>
                <div tw='relative w-full flex flex-col'>
                  <FormLabel htmlFor='startDate'>Duration</FormLabel>
                  <Spacer direction='vertical' size='xxs' />
                  <div tw='flex absolute h-10 w-10 z-10 top-[44px]'>
                    <DatePicker
                      tw='h-0 w-0 z-0 p-0 m-0'
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      selectsRange={true}
                      ref={datePickerRef}
                      startDate={dateRange[0]}
                      endDate={dateRange[1]}
                      onChange={(update: [Date | null, Date | null]): void => {
                        setDateRange(update);
                      }}
                    />
                  </div>
                  <Dropdown
                    tw='h-[35px] w-full'
                    onOptionClick={(val) => handleDateRangeChange(val as IDropdownOption)}
                    onChange={() => {
                      // no need to do anything here
                    }}
                    value={campaignDuration}
                    options={campaignDurationOptions}
                    data-testid='create-tag-form-insertion'
                    id='duration'
                    dropdownMenuStyle={tw`w-full`}></Dropdown>
                </div>

                <div tw='flex flex-col w-full'>
                  <FormLabel htmlFor='insertion'>Insertion</FormLabel>
                  <Spacer direction='vertical' size='xxs' />
                  <Dropdown
                    tw='h-[35px]'
                    onChange={(val) => setInsertion(val as IDropdownOption)}
                    value={insertion}
                    options={insertionOptions}
                    data-testid='create-tag-form-insertion'
                    id='insertion'
                    dropdownMenuStyle={tw`w-full`}
                  />
                </div>
                <div>
                  <FormLabel htmlFor='capped' tw='text-[13px]'>
                    Frequency Cap
                    <span tw='text-text-and-icon-muted font-normal ml-1'>(optional)</span>
                  </FormLabel>
                  <div tw='text-xs whitespace-nowrap mt-2 text-text-and-icon'>
                    <span>Max of</span>
                    <Input
                      inline={true}
                      tw='w-[60px] max-w-[60px] h-[29px] input-no-arrows bg-page-background mx-[4px] placeholder:text-text-and-icon-muted'
                      type='number'
                      placeholder='#'
                      value={capMaxImpressions}
                      onChange={(e) =>
                        setCapMaxImpressions((e.currentTarget as HTMLInputElement).value)
                      }
                    />
                    <span>impressions every</span>
                    <Input
                      inline={true}
                      tw='w-[60px] max-w-[60px] h-[29px] input-no-arrows bg-page-background mx-[4px] placeholder:text-text-and-icon-muted'
                      type='number'
                      placeholder='#'
                      onChange={(e) =>
                        setCapDaysInterval((e.currentTarget as HTMLInputElement).value)
                      }
                    />
                    day(s)
                  </div>
                </div>
              </FormInputs>
            </FormInputsContainer>
          </div>
          <div tw='flex justify-between py-[12px] px-[24px] bg-neutral-background'>
            <ControlButton
              disabled={!isFlightpathCalculationEnabled}
              onClick={(e) => handleCalculateAvails(e as unknown as MouseEvent)}
              tw='border border-accent-muted text-xs text-accent-muted bg-page-background'
              css={[
                !isFlightpathCalculationEnabled &&
                  tw`opacity-50 hover:opacity-50 cursor-not-allowed`,
              ]}
              data-testid=''>
              {lastSentParams ? 'Re-calculate Results' : 'Calculate Results'}
            </ControlButton>
            {(isNumber(totalAvails) || isLoading) && lastSentParams === currentParams && (
              <div tw='flex items-center text-sm gap-[4px] text-text-and-icon'>
                {isLoading ? (
                  <Loader theme='light' size='sm' />
                ) : (
                  <span tw='text-accent-muted text-text-and-icon'>
                    {Math.round(totalAvails || 0).toLocaleString('en-US')}
                  </span>
                )}
                {t('insights.integrations.megaphone.flightpath.footer_message')}
              </div>
            )}
          </div>
        </div>
      </FromInnerWrapper>
    );
  }

  return (
    <Wrapper>
      <TagInfoContainer>
        <Title>{display_name || ''}</Title>
        <Spacer direction='vertical' size='xs' />
        {isEditingDescription ? (
          <>
            <TextArea ref={descriptionInputRef} defaultValue={description || ''} />
            <Spacer direction='vertical' size='xs' />
            <div tw='w-full flex items-center justify-end gap-[8px]'>
              <Button.Secondary
                disabled={isDescriptionLoading}
                onClick={() => setIsEditingDescription(!isEditingDescription)}>
                Cancel
              </Button.Secondary>
              <Button.Primary onClick={handleUpdateDescription} disabled={isDescriptionLoading}>
                {isDescriptionLoading ? <Loader theme='dark' size='xs' /> : 'Save Edits'}
              </Button.Primary>
            </div>
          </>
        ) : (
          <div tw='flex gap-[8px] items-start'>
            <Description>{description || 'No description'}</Description>{' '}
            <button onClick={() => setIsEditingDescription(!isEditingDescription)}>
              <EditIcon tw='stroke-text-and-icon cursor-pointer' />
            </button>
          </div>
        )}
        <Spacer direction='vertical' size='sm' />
        <Flex>
          <Code>{id}</Code>
          <ActionButton tw='ml-2' onClick={handleCopyToClipboard}>
            {copiedTag ? (
              <CheckMarkIcon tw='stroke-dark-accent w-4 h-4' />
            ) : (
              <CopyIcon tw='stroke-dark-accent' />
            )}
          </ActionButton>
        </Flex>
      </TagInfoContainer>

      <SegmentCreator />
    </Wrapper>
  );
};
